import {
  Anchor,
  AspectRatio,
  Badge,
  Group,
  Image,
  Skeleton,
  Stack,
  Table,
  Text,
  Title
} from '@mantine/core'
import { IconArrowRight } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router'
import ImagePlaceholder from '@/assets/image-placeholder.png'
import { EventTagList } from '@/components/events/EventTagList/EventTagList'
import {
  extractObjectName,
  extractRoiName
} from '@/components/events/helpers/extractDetections'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useGetCameraEvents } from '@/queries/eventQueries'
import { ApplicationNestedPath, buildAppLink } from '@/router/paths'
import { Camera } from '@/types/camera'
import { getRelativeTimeFromNow } from '@/utils/date'

type DeploymentRecentEventsTableProps = {
  appId: string
  camera: Camera
}

export const DeploymentRecentEventsTable = ({
  appId,
  camera
}: DeploymentRecentEventsTableProps) => {
  const {
    data: eventsData,
    isLoading,
    isError
  } = useGetCameraEvents(appId, camera.camera_id, 5)

  const eventsLink = buildAppLink(appId, ApplicationNestedPath.events)

  return (
    <Stack gap="xs" mt="lg">
      <Group justify="space-between">
        <Title order={6}>
          <FormattedMessage
            id="deployments.details.camera.recentEvents"
            values={{ cameraName: camera.camera_name }}
          />
        </Title>

        <Anchor component={Link} size="sm" to={eventsLink}>
          <Group gap={4}>
            <FormattedMessage id="deployments.details.camera.recentEvents.viewAll" />
            <IconArrowRight size={14} />
          </Group>
        </Anchor>
      </Group>

      {isLoading && (
        <Stack gap="xs">
          <Skeleton height={24} width="40%" />
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} height={56} />
          ))}
        </Stack>
      )}

      {isError && (
        <PlatformAlert variant="error">
          <FormattedMessage id="deployments.details.camera.recentEvents.error" />
        </PlatformAlert>
      )}

      {eventsData && eventsData.results.length === 0 && (
        <Text c="dimmed" size="sm">
          <FormattedMessage id="deployments.details.camera.recentEvents.empty" />
        </Text>
      )}

      {eventsData && eventsData.results.length > 0 && (
        <Table verticalSpacing="xs" withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={200} />
              <Table.Th>
                <FormattedMessage id="deployments.details.camera.recentEvents.timestamp" />
              </Table.Th>
              <Table.Th>
                <FormattedMessage id="deployments.details.camera.recentEvents.detections" />
              </Table.Th>
              <Table.Th>
                <FormattedMessage id="deployments.details.camera.recentEvents.tags" />
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {eventsData.results.map((event) => (
              <Table.Tr key={event.id}>
                <Table.Td w={200}>
                  <AspectRatio ratio={16 / 9} w={160}>
                    <Image
                      fit="cover"
                      radius="sm"
                      src={
                        'recordings' in event
                          ? (event.recordings[0]?.thumbnail_path ?? '')
                          : ''
                      }
                      fallbackSrc={ImagePlaceholder}
                    />
                  </AspectRatio>
                </Table.Td>

                <Table.Td>
                  <Text size="sm">
                    {getRelativeTimeFromNow(event.timestamp.$date)}
                  </Text>
                </Table.Td>

                <Table.Td>
                  <Stack gap={4}>
                    <Badge size="sm" variant="filled">
                      {extractObjectName(event.detections)}
                    </Badge>
                    <Text c="dimmed" size="xs">
                      {extractRoiName(event.detections)}
                    </Text>
                  </Stack>
                </Table.Td>

                <Table.Td>
                  {'tags' in event && event.tags.length > 0 ? (
                    <EventTagList badgeSize="md" tags={event.tags} />
                  ) : (
                    <Text c="dimmed" size="xs">
                      -
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}
